import {
  format,
  addHours,
  addDays,
  subHours,
  subDays,
  subWeeks,
  subMonths,
  differenceInSeconds,
  isDate,
  formatDuration,
  intervalToDuration,
  isValid,
  isToday,
  isYesterday,
  startOfDay,
  startOfYesterday,
  startOfWeek,
  startOfMonth,
  startOfYear,
  getYear,
  getDay,
  parse,
  isFuture,
} from 'date-fns';

import { getRootStore } from 'models/root';
import { DAY_MS, HOUR_MS, MINUTE_MS, OFFLINE_TIME_MAX_MS, DAY_IN_SECONDS } from 'config/constants';
import i18n from 'i18next';

const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const dateUtilities = {
  convertUTCToLocal: timeHM =>
    format(new Date(`${new Date().toISOString().split('T')[0]}T${timeHM}:00.000Z`), 'p'),

  convertToken: timeHM =>
    format(new Date(`${new Date().toISOString().split('T')[0]}T${timeHM}:00.000`), 'p'),

  getDateInDays: days => {
    const startDate = new Date();
    const endDate = startDate.setDate(startDate.getDate() + days);
    return format(new Date(endDate), 'y-MM-d');
  },

  makeAgeDaysHours: startDate => {
    const difference = differenceInSeconds(new Date(new Date().toISOString()), new Date(startDate));
    const days = Math.floor(difference / 86400);
    const leftSeconds = difference % 86400;
    const hours = Math.floor(leftSeconds / 3600);
    return `${days}d ${hours}h`;
  },

  getTimestampToNowInSeconds: startDate => {
    return differenceInSeconds(new Date().toISOString(), startDate);
  },

  makeDateFormatMD: date => {
    return date ? format(new Date(date).getTime(), 'M/d') : 'N/A';
  },

  makeDuration: (startDate, finishDate) => {
    if (!startDate || !finishDate) {
      return '-';
    }
    const difference = differenceInSeconds(new Date(finishDate), new Date(startDate));
    if (difference && Number.isInteger(difference) && difference < 0) {
      return '-';
    }
    if (difference > 86400) {
      const days = Math.floor(difference / 86400);
      const leftSeconds = difference % 86400;
      const hours = Math.floor(leftSeconds / 3600);
      return `${days}d ${hours}h`;
    }
    const leftSecondsForMinutes = difference % 3600;
    const seconds = difference % 60;
    const hours = Math.floor(difference / 3600);
    const minutes = Math.floor(leftSecondsForMinutes / 60);
    if (hours && hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes && minutes > 0) {
      return `${minutes}m`;
    }
    if (seconds && seconds > 0) {
      return `${seconds}s`;
    }
    if (seconds === 0) {
      return `${seconds}s`;
    }
    return '-';
  },

  formatDate: date => {
    if (!date || !isDate(new Date(date))) {
      return '-';
    }
    return format(new Date(date), 'h:mm a');
  },

  formatTimeTo12Hour: timeStr => {
    const parsedTime = parse(timeStr, 'HH:mm:ss', new Date());
    return format(parsedTime, 'h:mm a');
  },

  getDifference: (startDate, finishDate = new Date()) => {
    if (!startDate) return {};
    const wrapTime = time => `0${time}`.slice(-2);

    const from = new Date(startDate);
    const to = new Date(finishDate);

    const differenceMS = to.getTime() - from.getTime();

    const difference = Math.round(differenceMS / 1000);

    const seconds = difference % 60;
    const minutes = Math.floor((difference % 3600) / 60);
    const hours = Math.floor(difference / 3600);

    const timerString = `${wrapTime(hours)}:${wrapTime(minutes)}:${wrapTime(seconds)}`;

    return {
      timerString,
      differenceMS,
      seconds,
      minutes,
      hours,
      isFuture: differenceMS < 0,
      isPast: differenceMS > 0,
    };
  },

  makeDateFormatMDYYYY: date => {
    return date ? format(new Date(date).getTime(), 'M/d/u') : null;
  },

  makeDateFormatMDYY: date => {
    return date ? format(new Date(date).getTime(), 'M/d/yy') : null;
  },

  durationToSeconds: duration => {
    const { days, hours, minutes, seconds } = duration;
    let total = 0;
    if (days) total += days * 24 * 60 * 60;
    if (hours) total += hours * 60 * 60;
    if (minutes) total += minutes * 60;
    if (seconds) total += seconds;

    return total;
  },

  secondsToDuration: seconds => {
    if (!seconds) return '0s';
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
    const { months, days, hours, minutes } = duration;

    const format = months
      ? ['months', 'days']
      : days
      ? ['days', 'hours']
      : hours > 0
      ? ['hours', 'minutes']
      : minutes > 0
      ? ['minutes', 'seconds']
      : ['seconds'];

    return formatDuration(duration, { format })
      .replace(' years', i18n.t('utils.dateUtilities.shortenedYear'))
      .replace(' year', i18n.t('utils.dateUtilities.shortenedYear'))
      .replace(' months', i18n.t('utils.dateUtilities.shortenedMonth'))
      .replace(' month', i18n.t('utils.dateUtilities.shortenedMonth'))
      .replace(' days', i18n.t('utils.dateUtilities.shortenedDay'))
      .replace(' day', i18n.t('utils.dateUtilities.shortenedDay'))
      .replace(' hours', i18n.t('utils.dateUtilities.shortenedHour'))
      .replace(' hour', i18n.t('utils.dateUtilities.shortenedHour'))
      .replace(' minutes', i18n.t('utils.dateUtilities.shortenedMinute'))
      .replace(' minute', i18n.t('utils.dateUtilities.shortenedMinute'))
      .replace(' seconds', i18n.t('utils.dateUtilities.shortenedSecond'))
      .replace(' second', i18n.t('utils.dateUtilities.shortenedSecond'));
  },

  /**
   * Should be used if date string is transformed into "no time zone format": timestamp.replace('Z', '')
   * Otherwise, it's leading to unexpected date shifting based on establishment's offset
   */
  convertUtcToZonedTime: (date, fallback = null) => {
    const root = getRootStore();

    // * Version where we use _pg_timezone_names_utc_offset - probably more stable
    if (isValid(new Date(date))) {
      return addHours(
        new Date(date),
        root.establishmentStore.establishment?._pg_timezone_names_utc_offset.hours,
      );
    }

    // * Version where we use utcToZonedTime func
    // if (isValid(new Date(date))) {
    //   return utcToZonedTime(new Date(date), root.establishmentStore.establishment.time_zone);
    // }
    return fallback;
  },

  convertUTCToZonedTime: (date, fallback = null) => {
    const root = getRootStore();

    if (isValid(new Date(date))) {
      return addHours(
        new Date(date.slice(0, -1)),
        root.establishmentStore.establishment?._pg_timezone_names_utc_offset.hours,
      );
    }

    return fallback;
  },

  convertZonedToUtcTime: (date, fallback = null) => {
    const root = getRootStore();

    if (isValid(new Date(date))) {
      return subHours(
        new Date(date),
        root.establishmentStore.establishment?._pg_timezone_names_utc_offset.hours,
      );
    }

    return fallback;
  },

  convertTimeToEstablishmentOffset: date => {
    const root = getRootStore();

    if (isValid(new Date(date))) {
      const utc_offset = new Date().getTimezoneOffset() / 60;
      const establishmentOffset =
        root.establishmentStore.establishment?._pg_timezone_names_utc_offset.hours;

      const d = new Date(date);
      return new Date(d.setHours(d.getHours() + utc_offset + establishmentOffset));
    }

    return null;
  },

  _isValidEstablishmentTimeZone: () => {
    const root = getRootStore();
    const establishmentTimeZone = root?.establishmentStore?.establishment.time_zone;
    if (!establishmentTimeZone) return false;

    try {
      new Intl.DateTimeFormat('en-US', { timeZone: establishmentTimeZone });
      return true;
    } catch (e) {
      if (e instanceof RangeError) {
        return false;
      }
    }
  },

  _getEstTimezoneShortName: () => {
    const root = getRootStore();

    const establishmentTimeZone = root?.establishmentStore?.establishment.time_zone;

    if (!dateUtilities._isValidEstablishmentTimeZone()) return establishmentTimeZone;

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: establishmentTimeZone,
      timeZoneName: 'short',
    });
    const shortDate = formatter.format(new Date());
    const [, timeZone] = shortDate.split(', ');

    return timeZone;
  },

  timeZoneShortNameIfDiff: (start = ' (', end = ')') => {
    const timeZoneShortName = dateUtilities._getEstTimezoneShortName();

    return start + timeZoneShortName + end;
  },

  makeDateFormatddMMMMyyyy: date => {
    const root = getRootStore();

    let convertedDate;
    if (isValid(new Date(date))) {
      convertedDate = subHours(
        new Date(date),
        root.establishmentStore.establishment?._pg_timezone_names_utc_offset.hours,
      );
    }
    return convertedDate ? format(new Date(convertedDate).getTime(), 'dd MMMM yyyy') : null;
  },

  isNotThisYear: date => {
    const currentYear = new Date();
    const yearOfDate = getYear(date);

    return yearOfDate !== getYear(currentYear);
  },

  formatDateIncludingToday: (rawDate, options) => {
    const {
      fallback = 'N/A',
      withTimezone = true,
      dateFormat = 'dateFormats.Md',
      customFormat = null,
    } = options || {};

    let date = new Date(rawDate);

    if (!rawDate || date.toString() === 'Invalid Date') return fallback;

    const root = getRootStore();

    const timeZoneShortName = dateUtilities._getEstTimezoneShortName();
    const timeZoneShortNameIfDiff = withTimezone ? ` (${timeZoneShortName})` : '';

    // Needed for compensate date-fns converting offset
    const localTimeZoneOffset = new Date().getTimezoneOffset() / 60;
    date = addHours(new Date(date), localTimeZoneOffset);

    // Apply establishment offset
    date = addHours(
      new Date(date),
      root.establishmentStore.establishment?._pg_timezone_names_utc_offset.hours,
    );

    if (customFormat) {
      return `${format(new Date(date), customFormat)} ${timeZoneShortNameIfDiff}`;
    }

    if (isToday(new Date(date))) {
      return i18n.t('utils.dateUtilities.todayAt', {
        date: format(new Date(date), 'h:mm a'),
        timezone: timeZoneShortNameIfDiff,
      });
    } else if (isYesterday(new Date(date))) {
      return i18n.t('utils.dateUtilities.yesterdayAt', {
        date: format(new Date(date), 'h:mm a'),
        timezone: timeZoneShortNameIfDiff,
      });
    } else if (dateUtilities.isNotThisYear(new Date(date))) {
      return `${format(new Date(date), 'M/d/yyyy')} ${i18n.t('utils.dateUtilities.at')} ${format(
        new Date(date),
        'h:mm a',
      )} ${timeZoneShortNameIfDiff}`;
    } else {
      return `${format(new Date(date), i18n.t(dateFormat))} ${i18n.t(
        'utils.dateUtilities.at',
      )} ${format(new Date(date), 'h:mm a')} ${timeZoneShortNameIfDiff}`;
    }
  },
  makeDisplayedTime: value => {
    if (!value) return 'N/A';
    return value.days
      ? i18n.t('utils.dateUtilities.displayedDaysTime', {
          days: value.days,
          hours: value.hours ? value.hours : 0,
        })
      : value.hours
      ? i18n.t('utils.dateUtilities.displayedHoursTime', {
          hours: value.hours,
          minutes: value.minutes ? value.minutes : 0,
        })
      : value.minutes
      ? i18n.t('utils.dateUtilities.displayedMinutesTime', {
          minutes: value.minutes,
          seconds: value.seconds ? value.seconds : 0,
        })
      : value.seconds
      ? i18n.t('utils.dateUtilities.displayedSecondsTime', {
          seconds: value.seconds,
        })
      : value.milliseconds
      ? i18n.t('utils.dateUtilities.displayedMillisecondsTime', {
          milliseconds: value.milliseconds,
        })
      : 'N/A';
  },

  parseTimeStringToSeconds: timeStr => {
    if (!timeStr) return timeStr;

    const str = timeStr.replace('days', 'day');

    const regex = new RegExp('(([0-9]*) day )?([0-9]{1,3}):([0-9]{2}):([0-9.]*)', 'g');
    let result = null;
    const matched = regex.exec(str);

    if (Array.isArray(matched)) {
      const [days, hours, minutes, seconds] = matched.slice(2);

      result =
        24 * 3600 * (days || 0) + 3600 * Number(hours) + 60 * Number(minutes) + Number(seconds);
    }

    return result;
  },

  isOnline: value => {
    if (!value) return false;

    if (typeof value === 'number') return value * 1000 < OFFLINE_TIME_MAX_MS; // expect number of seconds ago

    if (!Number.isNaN(Date.parse(value)))
      return Date.now() - Date.parse(value) < OFFLINE_TIME_MAX_MS; // expect ISO date string

    return false;
  },
  getMostRecentDate: dates =>
    dates
      .filter(timestamp => new Date(timestamp).getTime() !== null)
      .reduce((min, timestamp) => (min === null || timestamp > min ? timestamp : min), null),

  getYesterday: () => new Date(Date.now() - DAY_MS + new Date().getTimezoneOffset() * MINUTE_MS),

  revertOffsets: date => {
    if (!date) return date;
    const root = getRootStore();
    const {
      rotation: { hours },
    } = root.reportStore.report_configuration;
    const establishmentOffset =
      root.establishmentStore.establishment?._pg_timezone_names_utc_offset.hours;

    const localeOffsetMS = new Date().getTimezoneOffset() * MINUTE_MS;
    const rotationMS = hours ? hours * HOUR_MS : 0;
    const estOffsetMS = establishmentOffset ? establishmentOffset * HOUR_MS : 0;

    return new Date(Date.parse(date) + localeOffsetMS - rotationMS + estOffsetMS);
  },

  getDatesByPeriod: (period = 'yesterday', { fromDate = null, toDate = null } = {}) => {
    const periods = [
      'today',
      'yesterday',
      'this-week',
      'last-week',
      'this-month',
      'last-month',
      'ytd',
      'custom',
      '1',
      '7',
      '14',
      '30',
      'inventory_consumption_days',
    ];
    if (!periods.includes(period)) return { from: null, to: null };

    const root = getRootStore();
    const {
      day_week_begins,
      rotation: { hours },
      inventory_consumption_days,
    } = root.reportStore.report_configuration;
    const establishmentOffset =
      root.establishmentStore.establishment?._pg_timezone_names_utc_offset.hours;

    const localeOffsetMS = new Date().getTimezoneOffset() * MINUTE_MS;
    const rotationMS = hours ? hours * HOUR_MS : 0;
    const estOffsetMS = establishmentOffset ? establishmentOffset * HOUR_MS : 0;
    const typeHandlers = {
      today: startOfDay,
      yesterday: startOfYesterday,
      'this-week': startOfWeek,
      'last-week': startOfWeek,
      'this-month': startOfMonth,
      'last-month': startOfMonth,
      ytd: startOfYear,
      1: startOfDay,
      7: startOfDay,
      14: startOfDay,
      30: startOfDay,
      inventory_consumption_days: startOfDay,
    };
    const isNumberPeriod = ['1', '7', '14', '30'].includes(period);

    const handleOffsets = date => {
      return new Date(Date.parse(date) - localeOffsetMS + rotationMS - estOffsetMS).toISOString();
    };

    const getInitDate = () => {
      const fn = typeHandlers[period];
      if (!fn) return null;

      let options = {};

      if (period.includes('week')) {
        options.weekStartsOn = day_week_begins + 1;
      }

      return handleOffsets(fn(Date.now(), options));
    };
    const now = new Date();

    let from = isNumberPeriod ? null : getInitDate();
    let to = isNumberPeriod ? getInitDate() : null;

    if (period === 'yesterday') {
      to = addDays(from, 1).toISOString();
    }

    if (period === 'last-week') {
      to = from;
      from = subWeeks(from, 1).toISOString();
    }

    if (period === 'last-month') {
      to = from;
      from = subMonths(from, 1).toISOString();
    }

    if (period === 'custom') {
      from = fromDate ? handleOffsets(startOfDay(fromDate)) : null;
      to = toDate ? handleOffsets(startOfDay(addDays(toDate, 1))) : null;
    }

    if (period === 'inventory_consumption_days') {
      from = subDays(from, inventory_consumption_days).toISOString();
    }

    if (isNumberPeriod) {
      from = subDays(now, Number(period)).toISOString();
      to = now.toISOString();
    }

    if (isFuture(to)) to = null;

    return { from, to };
  },

  calculateRemainingTime: (date, period = DAY_IN_SECONDS) => {
    const difference = differenceInSeconds(new Date(), new Date(date));
    const left = period - difference;
    const seconds = left % 60;
    const hours = Math.floor(left / 3600);
    const minutes = Math.floor((left % 3600) / 60);
    const displayedHours = hours < 10 ? `0${hours}` : hours;
    const displayedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const displayedSeconds = seconds < 10 ? `0${seconds}` : seconds;
    const time = `${displayedHours}:${displayedMinutes}:${displayedSeconds}`;

    return time;
  },

  getDayWeekNumber: day => {
    const weekDays = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 7,
    };

    return weekDays[day.toLowerCase()];
  },

  getDeliveryFrequencyDays: (orderDay, deliveryDay) => {
    const orderIndex = days.findIndex(d => d.toLowerCase() === orderDay.toLowerCase());
    const deliveryIndex = days.findIndex(d => d.toLowerCase() === deliveryDay.toLowerCase());

    if (orderIndex === -1 || deliveryIndex === -1) return 0;

    const diff = (deliveryIndex - orderIndex + 7) % 7 || 7;
    return diff;
  },

  getDeliveryDayByFrequency: (orderDay, frequencyDays) => {
    if (!orderDay || !frequencyDays) return null;

    const orderIndex = days.findIndex(d => d.toLowerCase() === orderDay.toLowerCase());
    if (orderIndex === -1) return null;

    const deliveryIndex = (orderIndex + frequencyDays) % 7;
    return days[deliveryIndex];
  },

  getDifferenceBetweenDayNumbers: deliveryDay => {
    const today = getDay(new Date());

    if (deliveryDay < today) {
      return deliveryDay - today + 7;
    } else {
      return deliveryDay - today;
    }
  },
};

export default dateUtilities;
